import { useEffect, useMemo, useRef, useState } from "react";
import { Box, css, Flex } from "@storyofams/react-ui";
import { m } from "framer-motion";
import { useErrorHandler } from "react-error-boundary";
import { FormattedMessage, useIntl } from "react-intl";
import { Redirect, useHistory } from "react-router-dom";
import { Media, Title, Subtext, Button } from "~/components";
import config from "~/config";
import { convertCSStoObject } from "~/context/helper";
import { FlowNodeLayout, FlowNodeType } from "~/graphql/api/sdk";
import { useFlow, useIsMobile, useIsRtl, useSession } from "~/hooks";

import { messages } from "./messages";

import QuestionContentBlocksList from "~/components/QuestionContentBlocksList";

const MotionFlex = m(Flex);

const Start = () => {
  const handleError = useErrorHandler();
  const { flow } = useFlow();
  const history = useHistory();
  const intl = useIntl();
  const isRtl = useIsRtl();
  const { session, startSession } = useSession();
  const isMobile = useIsMobile();

  const continueRef = useRef(false);
  const initializingRef = useRef(true);
  const [isBusy, setBusy] = useState(false);

  const [advancedAnalyticsLoadSent, setAdvancedAnalyticsLoadSent] = useState(false);

  const flowNode = useMemo(() => flow.nodes?.[0], [flow]);

  const pageSettings = JSON.parse(flowNode?.pageSettings ?? "{}");

  const questionAlignment = pageSettings?.questionAlignment ?? "left";
  const descriptionAlignment = pageSettings?.descriptionAlignment ?? "left";
  const questionFontSize = pageSettings?.questionFontSize ?? "32";
  const descriptionFontSize = pageSettings?.descriptionFontSize ?? "16";

  const topWidthDesktopType = pageSettings?.topWidthDesktopType ?? "100%";
  const topWidth = pageSettings?.topWidth ?? "1200";
  const bottomWidthDesktopType = pageSettings?.bottomWidthDesktopType ?? "100%";
  const bottomWidth = pageSettings?.bottomWidth ?? "1200";
  const topWidthMobileType = pageSettings?.topWidthMobileType ?? "100%";
  const topWidthMobile = pageSettings?.topWidthMobile ?? "1200";
  const bottomWidthMobileType = pageSettings?.bottomWidthMobileType ?? "100%";
  const bottomWidthMobile = pageSettings?.bottomWidthMobile ?? "1200";

  const useDefaultColorForButton = pageSettings?.useDefaultColorForButton ?? "yes";
  const buttonColor = pageSettings?.buttonColor ?? "#000";
  const useDefaultTextColorForButton = pageSettings?.useDefaultTextColorForButton ?? "yes";
  const buttonTextColor = pageSettings?.buttonTextColor ?? "#fff";
  const buttonTextOverride = pageSettings?.buttonTextOverride ?? "";
  const buttonFontSize = pageSettings?.buttonFontSize ?? "18";
  const buttonBorderRadius = pageSettings?.buttonBorderRadius ?? "8";
  const buttonHorizontalPadding = pageSettings?.buttonHorizontalPadding ?? "56";
  const buttonVerticalPadding = pageSettings?.buttonVerticalPadding ?? "20";
  const buttonFontSizeMobile = pageSettings?.buttonFontSizeMobile ?? "18";
  const buttonBorderRadiusMobile = pageSettings?.buttonBorderRadiusMobile ?? "8";
  const buttonHorizontalPaddingMobile = pageSettings?.buttonHorizontalPaddingMobile ?? "56";
  const buttonVerticalPaddingMobile = pageSettings?.buttonVerticalPaddingMobile ?? "20";
  const buttonWidthMobileType = pageSettings?.buttonWidthMobileType ?? "100%";
  const buttonWidthMobile = pageSettings?.buttonWidthMobile ?? "345";
  const buttonWidthType = pageSettings?.buttonWidthType ?? "custom";
  const buttonWidth = pageSettings?.buttonWidth ?? "370";
  const buttonAlignment = pageSettings?.buttonAlignment ?? "left";

  const createSession = async () => {
    const success = await startSession(flow.id);

    if (success && continueRef.current) {
      history.push("/questions/1");
    } else {
      initializingRef.current = false;
      setBusy(false);
    }

    if (!success && flowNode?.type !== FlowNodeType.Welcome) {
      handleError(new Error(intl.formatMessage(messages.sessionError)));
    }
  };

  useEffect(() => {
    const sendHeightToParent = () => {
      const height = document.body.scrollHeight;
      if (height) {
        window?.parent?.postMessage(
          {
            action: "resize",
            frameHeight: height,
          },
          "*"
        );
      }
    };

    const images = document.querySelectorAll("img");
    images.forEach((image) => {
      if (image.complete) {
        sendHeightToParent();
      } else {
        image.addEventListener("load", sendHeightToParent);
        image.addEventListener("error", sendHeightToParent);
      }
    });
    window.addEventListener("load", sendHeightToParent);

    sendHeightToParent();

    const resizeObserver = new ResizeObserver(sendHeightToParent);
    resizeObserver.observe(document.body);

    return () => {
      resizeObserver.disconnect();
      images.forEach((image) => {
        image.removeEventListener("load", sendHeightToParent);
        image.removeEventListener("error", sendHeightToParent);
        window.removeEventListener("load", sendHeightToParent);
      });
    };
  }, [session]);

  useEffect(() => {
    if (session?.id) return;
    createSession();
  }, [session?.id, createSession]);

  useEffect(() => {
    if (session?.id && !advancedAnalyticsLoadSent) {
      /* advanced analytics */
      // load_welcome_screen
      if ((window as any)?.lantern_analytics_event) {
        (window as any).lantern_analytics_event({
          event: "load_welcome_screen",
          page: "welcome_screen",
          session_id: session.id,
        });
      }
      if ((window as any)?.lantern_gtag) {
        (window as any).lantern_gtag({
          event: "load_welcome_screen",
          page: "welcome_screen",
          session_id: session.id,
        });
      }

      setAdvancedAnalyticsLoadSent(true);
      /**********************/
    }
  }, [session]);

  if (flowNode?.type !== FlowNodeType.Welcome) {
    if (!session) {
      return null;
    }

    return <Redirect to="/questions/1" />;
  }

  const onStart = async () => {
    if (isBusy) {
      return;
    }

    setBusy(true);

    if (session?.id) {
      /* advanced analytics */
      // leave_welcome_screen
      if ((window as any)?.lantern_analytics_event) {
        (window as any).lantern_analytics_event({
          event: "leave_welcome_screen",
          page: "welcome_screen",
          session_id: session.id,
        });
      }
      if ((window as any)?.lantern_gtag) {
        (window as any).lantern_gtag({
          event: "leave_welcome_screen",
          page: "welcome_screen",
          session_id: session.id,
        });
      }
      /**********************/

      history.push("/questions/1");
    } else if (initializingRef.current) {
      continueRef.current = true;
    } else {
      const success = await startSession(flow.id);

      if (success) {
        history.push("/questions/1");
      } else {
        setBusy(false);
      }
    }
  };

  const getProps = (layout: FlowNodeLayout | FlowNodeLayout[], props) =>
    (Array.isArray(layout) ? layout.includes(flowNode.layout) : flowNode.layout === layout) ? props : {};

  const contentBlocks = useMemo(() => JSON.parse(flowNode?.contentBlocks ?? "{}"), [flowNode]);

  return (
    <>
      <Flex
        className="welcome-container"
        flexDirection="column"
        flex="1"
        alignItems="flex-start"
        {...getProps([FlowNodeLayout.MediaCoverRight, FlowNodeLayout.MediaCoverLeft], {
          justifyContent: ["flex-start", "center"],
        })}
      >
        <MotionFlex
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={config.transition}
          flexDirection="column"
          width="100%"
          {...getProps(FlowNodeLayout.MediaFloatTop, {
            textAlign: ["left", "center"],
            alignItems: ["flex-start", "center"],
          })}
          {...getProps(FlowNodeLayout.MediaFloatRight, {
            flexDirection: ["column", isRtl ? "row" : "row-reverse"],
            alignItems: ["flex-start", "center"],
          })}
          {...getProps(FlowNodeLayout.MediaFloatLeft, {
            flexDirection: ["column", isRtl ? "row-reverse" : "row"],
            alignItems: ["flex-start", "center"],
          })}
        >
          {(!!flowNode.image || !!flowNode.video) && (
            <>
              {flowNode.layout === FlowNodeLayout.MediaFloatTop && (
                <Box width="100%" mb={[4, 7]} mx="auto">
                  <Media
                    image={flowNode.image}
                    video={flowNode.video}
                    alt="cover image - start quiz"
                    minHeight="164px"
                    maxHeight="400px"
                    maxWidth={["530px", "790px"]}
                    mx="auto"
                    sizes="790px"
                  />
                </Box>
              )}

              {[FlowNodeLayout.MediaFloatRight, FlowNodeLayout.MediaFloatLeft].includes(flowNode.layout) && (
                <Box flex="1" width="100%" mb={[4, 0]}>
                  <Media
                    image={flowNode.image}
                    video={flowNode.video}
                    alt="cover image - start quiz"
                    width="500px"
                    minHeight="164px"
                    maxHeight={["40vh", "640px"]}
                    sizes="570px"
                    fullWidth
                  />
                </Box>
              )}
              {[FlowNodeLayout.MediaCoverRight, FlowNodeLayout.MediaCoverLeft].includes(flowNode.layout) && (
                <Box display={["block", "none"]} flex="1" mb={[4, 0]} mt="-16px" mx="-16px">
                  <Media
                    image={flowNode.image}
                    video={flowNode.video}
                    alt="cover image - start quiz"
                    minHeight="100%"
                    maxHeight="40vh"
                    sizes="570px"
                    fullWidth
                    cover
                  />
                </Box>
              )}
            </>
          )}

          <Box
            {...getProps(FlowNodeLayout.Simple, {
              mt: 5,
            })}
            {...getProps([FlowNodeLayout.MediaFloatRight, FlowNodeLayout.MediaFloatLeft], {
              flex: 1,
              mb: 4,
            })}
            width="100%"
          >
            <Box
              maxWidth={["530px", "790px"]}
              className="welcome-content"
              css={css({
                ...getProps(FlowNodeLayout.MediaFloatTop, {
                  mx: "auto",
                }),
                ...getProps(FlowNodeLayout.MediaFloatRight, {
                  pr: [0, "40px", "40px", "100px"],
                  textAlign: "start",
                }),
                ...getProps(FlowNodeLayout.MediaFloatLeft, {
                  pl: [0, "40px", "40px", "100px"],
                  textAlign: "start",
                }),
              })}
            >
              <m.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={config.transition}
                className="welcome-top-section"
                style={{
                  margin: "0 auto",
                  width: isMobile
                    ? topWidthMobileType === "100%"
                      ? "100%"
                      : `${topWidthMobile}px`
                    : topWidthDesktopType === "100%"
                    ? "100%"
                    : `${topWidth}px`,
                }}
              >
                <QuestionContentBlocksList contentBlocks={contentBlocks?.top} sessionVariables={null} />

                <Title
                  className="welcome-heading"
                  role="heading"
                  aria-level={1}
                  tabIndex={1}
                  aria-live="assertive"
                  style={{
                    fontSize: `${questionFontSize}px`,
                    textAlign: questionAlignment,
                    lineHeight: "1.2",
                    ...convertCSStoObject(flow?.cssEditor)[".welcome-heading"],
                  }}
                  {...getProps(FlowNodeLayout.MediaFloatTop, {
                    dir: "auto",
                  })}
                >
                  {flowNode?.title}
                </Title>
                <Subtext
                  className="welcome-subheading"
                  style={{
                    fontSize: `${descriptionFontSize}px`,
                    textAlign: descriptionAlignment,
                    lineHeight: "1.2",
                    ...convertCSStoObject(flow?.cssEditor)[".welcome-subheading"],
                  }}
                  mt={2}
                  mb={5}
                  {...getProps(FlowNodeLayout.MediaFloatTop, {
                    dir: "auto",
                  })}
                >
                  {flowNode?.description}
                </Subtext>

                <QuestionContentBlocksList contentBlocks={contentBlocks?.middle} sessionVariables={null} />
              </m.div>

              <m.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, ...config.transition }}
                style={{
                  margin: "0 auto",
                  width: isMobile
                    ? bottomWidthMobileType === "100%"
                      ? "100%"
                      : `${bottomWidthMobile}px`
                    : bottomWidthDesktopType === "100%"
                    ? "100%"
                    : `${bottomWidth}px`,
                }}
              >
                <m.div
                  className="welcome-button-container"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, ...config.transition }}
                  style={{
                    display: "flex",
                    marginTop: "20px",
                    justifyContent: buttonAlignment === "center" ? "center" : buttonAlignment === "right" ? "flex-end" : "flex-start",
                  }}
                >
                  <Button
                    arrow
                    mt={flowNode.layout === FlowNodeLayout.Simple ? 7 : 0}
                    width={
                      isMobile
                        ? buttonWidthMobileType === "100%"
                          ? "100%"
                          : buttonWidthMobileType === "auto"
                          ? "auto"
                          : `${buttonWidthMobile}px`
                        : buttonWidthType === "100%"
                        ? "100%"
                        : buttonWidthType === "auto"
                        ? "auto"
                        : `${buttonWidth}px`
                    }
                    onClick={onStart}
                    className="start-button"
                    isLoading={isBusy}
                    backgroundColor={useDefaultColorForButton === "no" ? buttonColor : undefined}
                    textColor={useDefaultTextColorForButton === "no" ? buttonTextColor : undefined}
                    style={
                      isMobile
                        ? {
                            ...(buttonFontSizeMobile >= 0
                              ? {
                                  fontSize: `${buttonFontSizeMobile}px`,
                                }
                              : {}),
                            ...(buttonBorderRadiusMobile >= 0
                              ? {
                                  borderRadius: `${buttonBorderRadiusMobile}px`,
                                }
                              : {}),
                            ...(buttonHorizontalPaddingMobile >= 0
                              ? {
                                  paddingLeft: `${buttonHorizontalPaddingMobile}px`,
                                  paddingRight: `${buttonHorizontalPaddingMobile}px`,
                                }
                              : {}),
                            ...(buttonVerticalPaddingMobile >= 0
                              ? {
                                  paddingTop: `${buttonVerticalPaddingMobile}px`,
                                  paddingBottom: `${buttonVerticalPaddingMobile}px`,
                                }
                              : {}),
                          }
                        : {
                            ...(buttonFontSize >= 0 ? { fontSize: `${buttonFontSize}px` } : {}),
                            ...(buttonBorderRadius >= 0
                              ? {
                                  borderRadius: `${buttonBorderRadius}px`,
                                }
                              : {}),
                            ...(buttonHorizontalPadding >= 0
                              ? {
                                  paddingLeft: `${buttonHorizontalPadding}px`,
                                  paddingRight: `${buttonHorizontalPadding}px`,
                                }
                              : {}),
                            ...(buttonVerticalPadding >= 0
                              ? {
                                  paddingTop: `${buttonVerticalPadding}px`,
                                  paddingBottom: `${buttonVerticalPadding}px`,
                                }
                              : {}),
                          }
                    }
                  >
                    {buttonTextOverride?.length > 0 ? buttonTextOverride : <FormattedMessage {...messages.start} />}
                  </Button>
                </m.div>

                <QuestionContentBlocksList contentBlocks={contentBlocks?.bottom} sessionVariables={null} />
              </m.div>
            </Box>
          </Box>
        </MotionFlex>
        <div className="end-of-quiz"></div>
      </Flex>
    </>
  );
};

export default Start;
